import React, { useCallback, useEffect, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import { MdAssignment, MdCheckCircle, MdFileDownload, MdPendingActions, MdUndo } from 'react-icons/md';
import {
  listIndents,
  exportIndentsExcel,
} from '../../../repositories/indent.repository';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import { indentStatusToApi } from '../../../models/indent.model';
import IndentDetailPage from '../equipments/indents/detail';
import {
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubKycBadge,
  HubLoading,
  HubMetricCard,
  HubPageHeader,
  HubTable,
} from './hubUi';

const OPEN_STATUSES = new Set([
  'Pending',
  'Recommended',
  'Assigned',
  'In progress',
]);

export function HubIndentsHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load indent counts');
      toast({ title: e.message || 'Failed to load indent counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const indents = data?.indents || {};
  const open =
    indents.open ??
    (Number(indents.pending) || 0) +
      (Number(indents.recommended) || 0) +
      (Number(indents.assigned) || 0) +
      (Number(indents.inProgress) || 0);

  return (
    <>
      <HubPageHeader title="Indents" />
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
        <HubMetricCard
          icon={MdAssignment}
          label="All requests"
          total={String(indents.total ?? 0)}
          metrics={[
            { label: 'Open', value: String(open) },
            { label: 'Released', value: String(indents.released ?? 0) },
            { label: 'Withdrawn', value: String(indents.cancelled ?? 0) },
          ]}
          onClick={() => push({ id: 'indentList', label: 'All indents', filter: 'all' })}
        />
        <HubMetricCard
          icon={MdPendingActions}
          label="Open"
          total={String(open)}
          totalLabel="Open"
          alert={
            (indents.pending ?? 0) > 0
              ? { label: 'pending action', value: indents.pending }
              : null
          }
          metrics={[
            { label: 'Pending', value: String(indents.pending ?? 0) },
            { label: 'Recs', value: String(indents.recommended ?? 0) },
            { label: 'Assigned', value: String(indents.assigned ?? 0) },
            { label: 'Progress', value: String(indents.inProgress ?? 0) },
          ]}
          onClick={() =>
            push({ id: 'indentList', label: 'Open', filter: 'open' })
          }
        />
        <HubCard
          icon={MdCheckCircle}
          label="Released"
          value={String(indents.released ?? 0)}
          hint="Completed indents"
          onClick={() =>
            push({ id: 'indentList', label: 'Released', filter: 'released' })
          }
        />
        <HubCard
          icon={MdUndo}
          label="Withdrawn"
          value={String(indents.cancelled ?? 0)}
          hint="Cancelled indents"
          onClick={() =>
            push({ id: 'indentList', label: 'Withdrawn', filter: 'withdrawn' })
          }
        />
      </SimpleGrid>
    </>
  );
}

export function HubIndentList({ push, filter = 'all', userId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(
    filter === 'pending'
      ? 'Pending'
      : filter === 'released'
        ? 'Released'
        : filter === 'withdrawn'
          ? 'Withdrawn'
          : '',
  );
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // "open" is a multi-status Hub slice — fetch all then filter client-side
      const apiStatus =
        filter === 'open'
          ? undefined
          : status
            ? indentStatusToApi(status)
            : undefined;
      const res = await listIndents({
        pageSize: 100,
        search: search.trim() || undefined,
        status: apiStatus,
        userId: userId || undefined,
      });
      let rows = res.items || [];
      if (filter === 'open') {
        rows = rows.filter((r) => OPEN_STATUSES.has(String(r.status || '')));
      }
      setItems(rows);
    } catch (e) {
      toast({ title: e.message || 'Failed to load indents', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search, status, userId, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportIndentsExcel({
        search: search.trim() || undefined,
        status: status ? indentStatusToApi(status) : undefined,
      });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Export failed', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <HubPageHeader
        title="Indents list"
        subtitle="Live requests with Hub filters. Open a row for lines, recommendations, and assignment."
        actions={
          <HubGhostButton
            leftIcon={<MdFileDownload />}
            onClick={handleExport}
            isLoading={exporting}
          >
            Export Excel
          </HubGhostButton>
        }
      />
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search request, requester…"
        filters={[
          {
            key: 'status',
            value: status,
            onChange: setStatus,
            options: [
              { value: '', label: 'All status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Recommended', label: 'Recommended' },
              { value: 'Assigned', label: 'Assigned' },
              { value: 'In progress', label: 'In progress' },
              { value: 'Released', label: 'Released' },
              { value: 'Withdrawn', label: 'Withdrawn' },
            ],
          },
        ]}
        onClear={() => {
          setSearch('');
          setStatus('');
        }}
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({
            id: 'indentDetail',
            label: r.requestNo || r.indentId || `REQ-${r.id}`,
            itemId: r.id,
          })
        }
        columns={[
          {
            key: 'requestNo',
            label: 'Request',
            render: (r) => r.requestNo || r.indentId || `REQ-${r.id}`,
          },
          {
            key: 'requester',
            label: 'Requester',
            render: (r) => r.requesterName || r.raisedByName || '—',
          },
          { key: 'itemCount', label: 'Items' },
          { key: 'summary', label: 'Summary' },
          {
            key: 'status',
            label: 'Status',
            render: (r) => <HubKycBadge status={r.status}>{r.status}</HubKycBadge>,
          },
        ]}
      />
    </>
  );
}

/** Classic indent detail UI embedded inside Hub. */
export function HubIndentDetail({ push, itemId }) {
  return (
    <IndentDetailPage
      indentId={itemId}
      embedded
      onOpenMember={(id, label) =>
        push({
          id: 'memberDetail',
          label: label || `User #${id}`,
          itemId: id,
        })
      }
      onOpenEquipment={(ueId) =>
        push({
          id: 'equipmentDetail',
          label: `UE-${ueId}`,
          itemId: ueId,
        })
      }
    />
  );
}
